import { useEffect, useMemo, useRef, useState } from "react";

import { analyticsCookieConsentApi } from "@/lib/api";
import {
  type AnalyticsCookieConsentMetadata,
  getAnalyticsCookieConsentMetadataFromPublicMetadata,
} from "@shared/constants";

import { useUser } from "@clerk/react";
import { usePostHog } from "posthog-js/react";

import {
  readAnalyticsConsentCookie,
  resolveAnalyticsConsent,
  writeAnalyticsConsentCookie,
} from "./analyticsConsentCookie";
import { isPosthogBrowserAnalyticsEnabled } from "./posthogBrowserEnabled";

/**
 * Keeps PostHog aligned with the shared browser cookie and Clerk-backed preference.
 * The newest record wins when the two stores disagree.
 */
export function PostHogAnalyticsConsentSync() {
  const posthog = usePostHog();
  const { user, isLoaded, isSignedIn } = useUser();
  const [cookieConsent, setCookieConsent] = useState<AnalyticsCookieConsentMetadata | null>(
    readAnalyticsConsentCookie
  );
  const syncInFlight = useRef<string | null>(null);

  const accountConsent = useMemo(() => {
    const meta = user?.publicMetadata as Record<string, unknown> | undefined;
    return getAnalyticsCookieConsentMetadataFromPublicMetadata(meta);
  }, [user?.publicMetadata]);

  const consent = useMemo(
    () => resolveAnalyticsConsent(cookieConsent, accountConsent),
    [accountConsent, cookieConsent]
  );

  useEffect(() => {
    if (!isLoaded) return;
    setCookieConsent(readAnalyticsConsentCookie());
  }, [isLoaded, isSignedIn, user?.publicMetadata]);

  useEffect(() => {
    if (!isPosthogBrowserAnalyticsEnabled || !import.meta.env.VITE_PUBLIC_POSTHOG_KEY || !posthog) {
      return;
    }
    if (!isLoaded) {
      return;
    }
    if (consent?.choice === "granted") {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  }, [consent, isLoaded, posthog]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return;

    if (!cookieConsent && accountConsent) {
      setCookieConsent(
        writeAnalyticsConsentCookie(accountConsent.choice, accountConsent.updatedAt)
      );
      return;
    }

    if (!cookieConsent) return;

    const cookieWins =
      !accountConsent ||
      (cookieConsent.choice !== accountConsent.choice &&
        resolveAnalyticsConsent(cookieConsent, accountConsent) === cookieConsent);

    if (!cookieWins) {
      if (accountConsent && cookieConsent.choice !== accountConsent.choice) {
        setCookieConsent(
          writeAnalyticsConsentCookie(accountConsent.choice, accountConsent.updatedAt)
        );
      }
      return;
    }

    const syncKey = `${cookieConsent.choice}:${cookieConsent.updatedAt}`;
    if (syncInFlight.current === syncKey) return;
    syncInFlight.current = syncKey;

    void analyticsCookieConsentApi
      .updateConsent(cookieConsent.choice === "granted" ? "grant" : "deny")
      .then(() => user.reload())
      .catch((error: unknown) => {
        console.error("analytics cookie consent account sync failed", error);
      })
      .finally(() => {
        if (syncInFlight.current === syncKey) syncInFlight.current = null;
      });
  }, [accountConsent, cookieConsent, isLoaded, isSignedIn, user]);

  return null;
}
